<template>
  <div class="trigger-settings">
    <!-- Header Section -->
    <div class="settings-header">
      <div class="header-content">
        <div class="header-title-section">
          <div class="header-icon">
            <i class="el-icon-setting"></i>
          </div>
          <div class="header-text">
            <h1 class="header-title">Trigger Configuration</h1>
            <p class="header-subtitle">
              Define when and how your automation workflows are activated
            </p>
          </div>
        </div>
        <div class="header-actions">
          <el-button class="help-btn" type="text" icon="el-icon-question">Help</el-button>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="settings-content">
      <!-- Basic Information Section -->
      <div class="settings-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="el-icon-document"></i>
          </div>
          <h2 class="section-title">Basic Information</h2>
        </div>
        <div class="section-content">
          <div class="form-grid">
            <div class="form-group">
              <label class="form-label required">
                <i class="el-icon-edit"></i>
                Trigger Name
              </label>
              <el-input
                v-model="localTrigger.name"
                placeholder="Enter a descriptive trigger name"
                size="small"
                class="modern-input"
                maxlength="50"
                show-word-limit
              />
              <div class="form-hint">
                This name will help you identify the trigger in your workflow
              </div>
            </div>

            <div class="form-group">
              <label class="form-label">
                <i class="el-icon-document"></i>
                Description
              </label>
              <el-input
                type="textarea"
                v-model="localTrigger.description"
                placeholder="Describe what this trigger does and when it activates"
                :rows="2"
                size="small"
                class="modern-input"
                resize="none"
                maxlength="200"
                show-word-limit
              />
              <div class="form-hint">Optional description for better documentation</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Trigger Conditions Section -->
      <div class="settings-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="el-icon-time"></i>
          </div>
          <h2 class="section-title">Trigger Conditions</h2>
        </div>
        <div class="section-content">
          <div class="trigger-configuration">
            <div class="trigger-main">
              <label class="form-label required">
                <i class="el-icon-caret-right"></i>
                Execute When
              </label>

              <div class="trigger-selector">
                <template
                  v-if="currentEntity?.whenActionSettings && currentEntity?.whenOptions?.length > 0"
                >
                  <el-select
                    v-model="localTrigger.when"
                    placeholder="Select trigger condition"
                    class="modern-select"
                    size="small"
                  >
                    <el-option
                      v-for="opt in filteredSelectedOptions"
                      :key="opt.value"
                      :label="opt.label"
                      :value="opt.value"
                    />
                  </el-select>
                </template>
                <template v-else>
                  <el-select
                    v-model="localTrigger.when"
                    placeholder="Select trigger condition"
                    class="modern-select"
                    size="small"
                  >
                    <el-option value="ADDED" label="📝 Record Added" />
                    <el-option value="NOTIFICATION" label="🔔 Notification Sent" />
                    <el-option value="DELETED" label="🗑️ Record Deleted" />
                    <el-option value="TRANSACTION" label="💰 Payment Recorded" />
                    <el-option value="AUTOMATION" label="🔄 Recurring Schedule" />
                    <el-option value="FILE_ADDED" label="📎 File Added" />
                    <el-option value="DATE_TIME" label="📅 Date/Time Field" />
                    <el-option-group label="Template Updated">
                      <el-option
                        v-for="(temp, i) in currentEntityTemplates"
                        :key="`template-${i}`"
                        :label="`📋 ${temp.name} Template updated`"
                        :value="`template/${temp._id}`"
                      />
                    </el-option-group>
                  </el-select>
                </template>
              </div>
            </div>

            <!-- Advanced Trigger Settings -->
            <div v-if="localTrigger.when" class="trigger-advanced">
              <!-- DATE_TIME Settings -->
              <div v-if="localTrigger.when === 'DATE_TIME'" class="advanced-panel">
                <div class="panel-header">
                  <h3 class="panel-title">Date/Time Configuration</h3>
                  <div class="panel-actions">
                    <el-radio-group v-model="schedule_type" class="schedule-type-selector">
                      <el-radio
                        v-if="!localTrigger.is_global_action"
                        label="date_field"
                        class="type-radio"
                      >
                        <i class="el-icon-date"></i>
                        Field-Based
                      </el-radio>
                      <el-radio label="Select_manually" class="type-radio">
                        <i class="el-icon-setting"></i>
                        Manual Schedule
                      </el-radio>
                    </el-radio-group>
                  </div>
                </div>

                <!-- Date Field Based Schedule -->
                <template v-if="schedule_type === 'date_field'">
                  <div class="date-field-config">
                    <div class="config-section">
                      <h4 class="config-title">Field Selection</h4>
                      <div class="config-content">
                        <div class="form-group">
                          <label class="form-label required">
                            <i class="el-icon-date"></i>
                            Date/Time Field
                          </label>
                          <el-select
                            v-model="schedule_data_id"
                            placeholder="Select a date or datetime field"
                            class="modern-select"
                            size="small"
                            @change="setScheduleData(schedule_data_id)"
                          >
                            <el-option
                              v-for="field in date_time_fields"
                              :key="field.id"
                              :value="field.id"
                              :label="field.label"
                            />
                          </el-select>
                        </div>
                      </div>
                    </div>

                    <div class="config-section" v-if="schedule_data">
                      <h4 class="config-title">Execution Settings</h4>
                      <div class="config-content">
                        <div class="form-row">
                          <div class="form-group">
                            <label class="form-label required">
                              <i class="el-icon-timer"></i>
                              Time Zone
                            </label>
                            <el-select
                              v-model="selected_time_zone"
                              placeholder="Select timezone"
                              filterable
                              size="small"
                              class="modern-select"
                            >
                              <el-option
                                v-for="(timezone, index) in time_zone"
                                :key="`tz-${index}`"
                                :label="timezone"
                                :value="timezone"
                              />
                            </el-select>
                          </div>

                          <div v-if="schedule_data?.inputType === 'DATE_TIME'" class="form-group">
                            <label class="form-label required">
                              <i class="el-icon-set-up"></i>
                              Match Type
                            </label>
                            <el-select v-model="selected_period" size="small" class="modern-select">
                              <el-option
                                v-for="(item, i) in scheduleExecutionPeriodOptions"
                                :key="`period-${i}`"
                                :label="item.label"
                                :value="item.value"
                              />
                            </el-select>
                          </div>
                        </div>

                        <!-- Execution Rules -->
                        <div class="execution-rules">
                          <!-- Date Execution Rule -->
                          <div class="rule-group">
                            <div class="rule-header">
                              <i class="el-icon-date"></i>
                              <span>Date Execution</span>
                            </div>
                            <div class="rule-controls">
                              <div class="control-row">
                                <el-select
                                  v-model="schedule_settings.executed_on_date"
                                  class="small-select"
                                  size="small"
                                >
                                  <el-option
                                    v-for="(item, i) in ['On', 'Before', 'After']"
                                    :key="`date-opt-${i}`"
                                    :label="item"
                                    :value="item"
                                  />
                                </el-select>

                                <template v-if="schedule_settings.executed_on_date === 'On'">
                                  <span class="field-reference">
                                    the same date as
                                    <strong>{{ schedule_data.label }}</strong>
                                  </span>
                                </template>
                                <template v-else>
                                  <div class="value-selector">
                                    <el-input-number
                                      v-model="schedule_settings.executed_on_date_value"
                                      :disabled="
                                        schedule_settings.executed_on_date_field ? true : false
                                      "
                                      class="date-input-number"
                                      type="number"
                                      size="small"
                                      :min="1"
                                      :max="30"
                                      controls-position="right"
                                    />
                                    <span class="option-divider">or</span>
                                    <el-select
                                      v-model="schedule_settings.executed_on_date_field"
                                      clearable
                                      class="small-select"
                                      size="small"
                                      placeholder="Use field"
                                    >
                                      <el-option
                                        v-for="(item, i) in getNumberFields"
                                        :key="`num-field-${i}`"
                                        :label="item.label"
                                        :value="item.key"
                                      />
                                    </el-select>
                                    <el-select
                                      v-model="schedule_settings.executed_on_date_period"
                                      class="small-select"
                                      :disabled="!schedule_settings.executed_on_date_value"
                                      size="small"
                                    >
                                      <el-option
                                        v-for="(item, i) in datePeriodOptions"
                                        :key="`date-period-${i}`"
                                        :label="item"
                                        :value="item"
                                      />
                                    </el-select>
                                  </div>
                                </template>
                              </div>
                            </div>
                          </div>

                          <!-- Time Execution Rule -->
                          <div
                            v-if="schedule_data.inputType === 'DATE_TIME' && timeExecutionVisible"
                            class="rule-group"
                          >
                            <div class="rule-header">
                              <i class="el-icon-time"></i>
                              <span>Time Execution</span>
                            </div>
                            <div class="rule-controls">
                              <div class="control-row">
                                <el-select
                                  v-model="schedule_settings.executed_on_time"
                                  class="small-select"
                                  size="small"
                                >
                                  <el-option
                                    v-for="(item, i) in ['On', 'Before', 'After']"
                                    :key="`time-opt-${i}`"
                                    :label="item"
                                    :value="item"
                                  />
                                </el-select>

                                <template v-if="schedule_settings.executed_on_time !== 'On'">
                                  <div class="value-selector">
                                    <el-input
                                      v-model="schedule_settings.executed_on_time_value"
                                      class="small-input"
                                      type="number"
                                      size="small"
                                      placeholder="Value"
                                    />
                                    <el-select
                                      v-model="schedule_settings.executed_on_time_period"
                                      class="small-select"
                                      :disabled="!schedule_settings.executed_on_time_value"
                                      size="small"
                                    >
                                      <el-option
                                        v-for="(item, i) in timePeriodOptions"
                                        :key="`time-period-${i}`"
                                        :label="item"
                                        :value="item"
                                      />
                                    </el-select>
                                  </div>
                                </template>
                                <template v-else>
                                  <span class="field-reference">
                                    the same time as
                                    <strong>{{ schedule_data.label }}</strong>
                                  </span>
                                </template>
                              </div>
                            </div>
                          </div>

                          <!-- Recurrence Settings -->
                          <div class="rule-group">
                            <div class="rule-header">
                              <i class="el-icon-refresh"></i>
                              <span>Recurrence</span>
                            </div>
                            <div class="rule-controls">
                              <div class="control-row">
                                <el-select
                                  v-model="schedule_settings.recurring_execution_type"
                                  class="small-select"
                                  size="small"
                                >
                                  <el-option
                                    v-for="(item, i) in recurrence_type"
                                    :key="`recurrence-${i}`"
                                    :label="item.label"
                                    :value="item.value"
                                  />
                                </el-select>

                                <template
                                  v-if="schedule_settings.recurring_execution_type !== 'once'"
                                >
                                  <div class="value-selector">
                                    <el-input-number
                                      v-model="schedule_settings.recurring_execution_value"
                                      :disabled="
                                        schedule_settings.recurring_execution_field ? true : false
                                      "
                                      class="date-input-number"
                                      type="number"
                                      size="small"
                                      :min="1"
                                      :max="30"
                                      controls-position="right"
                                    />
                                    <span class="option-divider">or</span>
                                    <el-select
                                      v-model="schedule_settings.recurring_execution_field"
                                      class="small-select"
                                      size="small"
                                      clearable
                                      placeholder="Use field"
                                    >
                                      <el-option
                                        v-for="(item, i) in getNumberFields"
                                        :key="`recur-field-${i}`"
                                        :label="item.label"
                                        :value="item.key"
                                      />
                                    </el-select>
                                    <el-select
                                      v-model="schedule_settings.recurring_period"
                                      class="small-select"
                                      :disabled="!schedule_settings.recurring_execution_value"
                                      size="small"
                                    >
                                      <el-option
                                        v-for="(item, i) in recurring_period_types"
                                        :key="`recur-period-${i}`"
                                        :label="item.label"
                                        :value="item.value"
                                      />
                                    </el-select>
                                  </div>

                                  <!-- Stopping Condition -->
                                  <div class="stopping-condition">
                                    <span class="stopping-label">Stop after</span>
                                    <el-input-number
                                      v-if="
                                        schedule_settings.recurring_stopping_condition === 'After'
                                      "
                                      v-model="schedule_settings.recurring_stopping_value"
                                      :disabled="schedule_settings.recurring_stopping_field"
                                      class="date-input-number"
                                      type="number"
                                      size="small"
                                      :min="1"
                                      :max="20"
                                      controls-position="right"
                                    />
                                    <span class="occurrences-text">occurrences</span>
                                  </div>
                                </template>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Manual Schedule Settings -->
                <template v-else>
                  <div class="manual-schedule-config">
                    <div class="config-header">
                      <h4 class="config-title">Manual Schedule Configuration</h4>
                      <div class="config-actions">
                        <el-button size="mini" @click="resetManualSchedule">
                          <i class="el-icon-refresh"></i>
                          Reset
                        </el-button>
                      </div>
                    </div>

                    <div class="schedule-grid">
                      <ScheduleCard
                        title="Minutes"
                        :items="minutes"
                        :checked-items="manual_schedule_settings.minutes"
                        :check-all="manual_schedule_settings.checkAllminutes"
                        :indeterminate="isminutesIndeterminate"
                        @check-all-change="handleCheckMinutesChange"
                        @checked-change="handleCheckedMinuteChange"
                      />

                      <ScheduleCard
                        title="Hours"
                        :items="hours"
                        :checked-items="manual_schedule_settings.hours"
                        :check-all="manual_schedule_settings.checkAllhours"
                        :indeterminate="ishoursIndeterminate"
                        @check-all-change="handleCheckHoursChange"
                        @checked-change="handleCheckedHourChange"
                      />

                      <ScheduleCard
                        title="Days"
                        :items="days"
                        :checked-items="manual_schedule_settings.days"
                        :check-all="manual_schedule_settings.checkAlldays"
                        :indeterminate="isdaysIndeterminate"
                        @check-all-change="handleCheckDaysChange"
                        @checked-change="handleCheckedDayChange"
                      />
                    </div>

                    <div class="schedule-grid compact">
                      <ScheduleCard
                        title="Days of Week"
                        :items="weeks"
                        :checked-items="manual_schedule_settings.days_of_week"
                        :check-all="manual_schedule_settings.checkAllweeks"
                        :indeterminate="isweeksIndeterminate"
                        @check-all-change="handleCheckWeeksChange"
                        @checked-change="handleCheckedWeekChange"
                        :compact="true"
                      />

                      <ScheduleCard
                        title="Months"
                        :items="months"
                        :checked-items="manual_schedule_settings.months"
                        :check-all="manual_schedule_settings.checkAllmonths"
                        :indeterminate="ismonthsIndeterminate"
                        @check-all-change="handleCheckMonthsChange"
                        @checked-change="handleCheckedMonthChange"
                        :compact="true"
                      />

                      <ScheduleCard
                        title="Years"
                        :items="years"
                        :checked-items="manual_schedule_settings.years"
                        :check-all="manual_schedule_settings.checkAllyears"
                        :indeterminate="isyearsIndeterminate"
                        @check-all-change="handleCheckYearsChange"
                        @checked-change="handleCheckedYearChange"
                        :compact="true"
                      />
                    </div>
                  </div>
                </template>
              </div>

              <!-- AUTOMATION Settings -->
              <div v-else-if="localTrigger.when === 'AUTOMATION'" class="advanced-panel">
                <div class="panel-header">
                  <h3 class="panel-title">Recurring Automation Settings</h3>
                </div>

                <div class="automation-config">
                  <div class="config-grid">
                    <div class="config-item">
                      <label class="form-label required">
                        <i class="el-icon-refresh"></i>
                        Recurrence Pattern
                      </label>
                      <div class="recurrence-controls">
                        <span class="control-prefix">Every</span>
                        <el-input-number
                          v-model="recurrence_settings.recurring_execution_value"
                          class="medium-input-number"
                          type="number"
                          size="small"
                          :min="1"
                          :max="30"
                          controls-position="right"
                        />
                        <el-select
                          v-model="recurrence_settings.recurring_period"
                          class="medium-select"
                          size="small"
                          @change="setExecutiontime"
                        >
                          <el-option
                            v-for="(item, i) in recurring_period_types"
                            :key="`auto-period-${i}`"
                            :label="item.label"
                            :value="item.value"
                          />
                        </el-select>
                      </div>
                    </div>

                    <div class="config-item">
                      <label class="form-label required">
                        <i class="el-icon-time"></i>
                        Start Time
                      </label>
                      <div class="time-controls">
                        <el-date-picker
                          v-model="recurrence_settings.execution_date"
                          type="date"
                          placeholder="Select start date"
                          size="small"
                          :disabled="datePickerDisabled"
                          format="MM/dd/yyyy"
                          :picker-options="pickerOptions"
                          class="medium-input"
                        />
                        <el-time-select
                          v-model="recurrence_settings.execution_time"
                          :picker-options="timePickerOptions"
                          size="small"
                          placeholder="Select time"
                          class="medium-input"
                        />
                      </div>
                    </div>

                    <div class="config-item">
                      <label class="form-label required">
                        <i class="el-icon-map-location"></i>
                        Time Zone
                      </label>
                      <el-select
                        v-model="recurrence_settings.zone"
                        class="full-width-select"
                        placeholder="Select timezone"
                        filterable
                        size="small"
                      >
                        <el-option
                          v-for="(timezone, index) in time_zone"
                          :key="`auto-tz-${index}`"
                          :label="timezone"
                          :value="timezone"
                        />
                      </el-select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Advanced Options Section -->
      <div class="settings-section">
        <div class="section-header">
          <div class="section-icon">
            <i class="el-icon-setting"></i>
          </div>
          <h2 class="section-title">Advanced Options</h2>
        </div>
        <div class="section-content">
          <div class="advanced-options">
            <template v-if="otherAddActions.length">
              <div class="option-item">
                <el-checkbox v-model="localTrigger.establishRelationship" class="modern-checkbox">
                  <div class="checkbox-content">
                    <div class="checkbox-icon">
                      <i class="el-icon-connection"></i>
                    </div>
                    <div class="checkbox-text">
                      <div class="checkbox-label">Establish Relationships</div>
                      <div class="checkbox-description">
                        Automatically create relationships between entities added through this
                        action
                      </div>
                    </div>
                  </div>
                </el-checkbox>
              </div>
            </template>

            <div class="option-item">
              <el-checkbox
                v-model="localTrigger.is_global_action"
                @change="handleGlobalActionChange"
                class="modern-checkbox"
              >
                <div class="checkbox-content">
                  <div class="checkbox-icon">
                    <i class="el-icon-global"></i>
                  </div>
                  <div class="checkbox-text">
                    <div class="checkbox-label">Entity Level Action</div>
                    <div class="checkbox-description">
                      Apply this action at the entity level rather than individual records
                    </div>
                  </div>
                </div>
              </el-checkbox>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="settings-footer">
      <div class="footer-content">
        <div class="footer-info">
          <div class="validation-status" :class="validationStatus">
            <i class="el-icon-circle-check" v-if="validationStatus === 'valid'"></i>
            <i class="el-icon-warning" v-else-if="validationStatus === 'warning'"></i>
            <i class="el-icon-error" v-else></i>
            {{ validationMessage }}
          </div>
        </div>
        <div class="footer-actions">
          <el-button @click="$emit('close')" class="cancel-btn" size="medium">
            <i class="el-icon-close"></i>
            Cancel
          </el-button>
          <el-button type="primary" @click="saveSettings" class="save-btn" size="medium">
            <i class="el-icon-check"></i>
            Save Configuration
          </el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import AutomationHelper from '../../../mixins/AutomationHelper';
  import { configs } from '@/assets/data/schedule_config.ts';

  // Constants
  const { weeksArray, monthsArray, timezonesArray } = configs;

  // Precompute arrays
  const minutesArray = Array.from({ length: 60 }, (_, i) => i);
  const hoursArray = Array.from({ length: 24 }, (_, i) => i);
  const daysArray = Array.from({ length: 31 }, (_, i) => i + 1);
  const yearsArray = Array.from({ length: 8 }, (_, i) => 2023 + i);

  // Reusable Schedule Card Component
  const ScheduleCard = {
    name: 'ScheduleCard',
    props: {
      title: String,
      items: Array,
      checkedItems: Array,
      checkAll: Boolean,
      indeterminate: Boolean,
      compact: Boolean,
    },
    emits: ['checkAllChange', 'checkedChange'],
    methods: {
      handleCheckAllChange(val) {
        this.$emit('checkAllChange', val);
      },
      handleCheckedChange(value) {
        this.$emit('checkedChange', value);
      },
    },
    template: `
    <div class="schedule-card" :class="{ compact }">
      <div class="schedule-card-header">
        <el-checkbox
          :indeterminate="indeterminate"
          :value="checkAll"
          @change="handleCheckAllChange"
          class="schedule-checkbox"
        >
          <i class="el-icon-time"></i>
          {{ title }}
        </el-checkbox>
      </div>
      <div class="schedule-card-content">
        <el-checkbox-group
          :value="checkedItems"
          @change="handleCheckedChange"
          :class="['checkbox-group', { compact }]"
        >
          <el-checkbox
            v-for="item in items"
            :key="item"
            :label="item"
            class="uniform-checkbox"
          >
            {{ item }}
          </el-checkbox>
        </el-checkbox-group>
      </div>
    </div>
  `,
  };

  export default {
    name: 'TriggerSettings',
    components: {
      ScheduleCard,
    },
    mixins: [AutomationHelper],
    props: {
      trigger: {
        type: Object,
        required: true,
      },
      currentEntity: {
        type: Object,
      },
    },
    data() {
      return {
        localTrigger: null,
        schedule_type: 'date_field',
        schedule_data: null,
        schedule_data_id: '',
        date_time_fields: [],
        allFields: [],
        selected_time_zone: 'America/New_York',
        time_zone: timezonesArray,
        selected_period: 'date_match',
        scheduleExecutionPeriodOptions: [
          { label: 'Full Match', value: 'full_match' },
          { label: 'Date Match', value: 'date_match' },
          { label: 'Time Match', value: 'time_match' },
          { label: 'Year Match', value: 'year_match' },
          { label: 'Month Match', value: 'month_match' },
          { label: 'Month Year Match', value: 'month_year_match' },
          { label: 'Day Month Match', value: 'day_month_match' },
        ],
        schedule_settings: {
          executed_on_date: 'On',
          executed_on_time: 'On',
          execution_time: '05:00',
          recurring_order: 'once',
          executed_on_date_value: '1',
          executed_on_date_field: '',
          executed_on_date_period: 'Day(s)',
          executed_on_time_value: '1',
          executed_on_time_period: 'Hour(s)',
          recurring_execution_type: 'once',
          recurring_execution_value: '5',
          recurring_execution_field: '',
          recurring_period: 'Day(s)',
          recurring_stopping_condition: 'After',
          time_zone: '',
          recurring_stopping_value: '2',
          recurring_stopping_field: '',
          schedule_period: 'month_match',
        },
        manual_schedule_settings: {
          minutes: [0],
          hours: [0],
          days: [1],
          months: [...monthsArray],
          days_of_week: [...weeksArray],
          years: [2024, 2025],
          checkAllminutes: false,
          checkAllhours: false,
          checkAlldays: false,
          checkAllweeks: true,
          checkAllmonths: true,
          checkAllyears: false,
          status: false,
          multiple: false,
          time_zone: '',
        },
        recurring_period_types: [
          { label: 'Hour(s)', value: 'hours' },
          { label: 'Day(s)', value: 'days' },
          { label: 'Month(s)', value: 'months' },
          { label: 'Year(s)', value: 'years' },
        ],
        otherAddActions: [],
        recurrence_type: [
          { label: 'Once', value: 'once' },
          { label: 'For Every', value: 'for_every' },
        ],
        recurrence_settings: {
          recurring_execution_value: '1',
          recurring_execution_field: '',
          recurring_period: 'days',
          execution_time: '05:00',
          execution_date: this.calculateDate(),
          zone: 'Asia/Kolkata',
        },
        isminutesIndeterminate: true,
        ishoursIndeterminate: true,
        isdaysIndeterminate: true,
        isweeksIndeterminate: true,
        ismonthsIndeterminate: true,
        isyearsIndeterminate: true,
        minutes: minutesArray,
        hours: hoursArray,
        days: daysArray,
        weeks: weeksArray,
        months: monthsArray,
        years: yearsArray,
        pickerOptions: {
          disabledDate: (time) => time.getTime() < Date.now() - 86400000,
        },
      };
    },
    computed: {
      ...mapGetters('auth', ['getUserType']),

      currentEntityTemplates() {
        return (this.currentEntity?.templates || []).flatMap((e) => e.templateInfo || []);
      },

      getNumberFields() {
        return this.allFields.filter((e) => e.inputType === 'NUMBER' && !e.data_table_key);
      },

      filteredSelectedOptions() {
        if (!this.currentEntity?.whenOptions?.length) return [];
        if (this.getUserType?.name !== 'OWNER') return [];

        const templateOptions = this.currentEntityTemplates.map((t) => ({
          label: `${t.name} Template`,
          value: `template/${t._id}`,
        }));

        const baseOptions = [
          { value: 'ADDED', label: 'Record Added' },
          { value: 'NOTIFICATION', label: 'Notification Received' },
          { value: 'DELETED', label: 'Record Deleted' },
          { value: 'TRANSACTION', label: 'Payment Recorded' },
          { value: 'AUTOMATION', label: 'Recurring Schedule' },
          { value: 'FILE_ADDED', label: 'File Uploaded' },
          { value: 'DATE_TIME', label: 'Date/Time Field' },
          ...templateOptions,
        ];

        return baseOptions.filter((opt) => this.currentEntity.whenOptions.includes(opt.value));
      },

      timePickerOptions() {
        const isHourly = this.recurrence_settings?.recurring_period === 'hours';
        return {
          start: '00:00',
          step: isHourly ? '00:05' : '00:15',
          end: isHourly ? '00:55' : '23:45',
        };
      },

      datePeriodOptions() {
        return ['Day(s)', 'Week(s)', 'Month(s)', 'Year(s)'];
      },

      timePeriodOptions() {
        return ['Minute(s)', 'Hour(s)'];
      },

      timeExecutionVisible() {
        return ['full_match', 'time_match'].includes(this.selected_period);
      },

      datePickerDisabled() {
        return ['days', 'hours'].includes(this.recurrence_settings.recurring_period);
      },

      validationStatus() {
        if (!this.localTrigger.name?.trim()) return 'error';
        if (!this.localTrigger.when) return 'warning';
        return 'valid';
      },

      validationMessage() {
        switch (this.validationStatus) {
          case 'error':
            return 'Trigger name is required';
          case 'warning':
            return 'Trigger condition not selected';
          default:
            return 'Configuration is valid';
        }
      },
    },
    created() {
      this.localTrigger = {
        ...this.trigger,
      };
    },
    mounted() {
      this.initializeComponent();
    },
    methods: {
      initializeComponent() {
        if (this.currentEntity?.templates) {
          this.getAllDateFields();
          this.getTimeZone();
        }
        switch (this.localTrigger.when) {
          case 'DATE_TIME':
            if (this.localTrigger.schedule_data) {
              this.schedule_data_id = this.localTrigger.schedule_data._id;
              this.schedule_data = this.localTrigger.schedule_data;
              this.schedule_type = this.localTrigger.schedule_type;
              this.schedule_settings = this.localTrigger.schedule_settings;
            }
            break;

          case 'AUTOMATION':
            if (this.localTrigger.recurrence_settings) {
              this.recurrence_settings = this.localTrigger.recurrence_settings;
            }
            break;

          default:
            // Optional: handle other cases
            break;
        }
      },

      saveSettings() {
        if (!this.localTrigger.name?.trim()) {
          this.$message.error('Please enter a trigger name');
          return;
        }
        let checkSettings;
        if (this.localTrigger.when == 'DATE_TIME' || this.localTrigger.when == 'DATE') {
          let settings =
            this.schedule_type == 'date_field'
              ? this.schedule_settings
              : this.manual_schedule_settings;
          checkSettings = this.validateScheduleSettings(
            this.localTrigger,
            this.schedule_type,
            settings,
            this.schedule_data
          );
          if (!checkSettings.status) {
            return this.$message({
              message: checkSettings.message || 'Warning, Please config required settings.',
              type: 'warning',
            });
          }
        }
        switch (this.localTrigger.when) {
          case 'DATE_TIME':
            this.localTrigger.schedule_data = this.schedule_data;
            this.localTrigger.schedule_type = this.schedule_type;
            this.localTrigger.schedule_settings = this.schedule_settings;
            break;

          case 'AUTOMATION':
            this.localTrigger.recurrence_settings = this.recurrence_settings;
            break;

          default:
            // Optional: handle other cases
            break;
        }

        this.$emit('update', this.localTrigger);
      },

      calculateDate() {
        return Date.now() + 86400000;
      },

      setScheduleData(schedule_data_id) {
        const field = this.date_time_fields.find((item) => item.id === schedule_data_id);
        this.schedule_data = field?.data || null;
      },

      async getAllDateFields() {
        this.allFields = this.getAllFieldsFromEntity(this.currentEntity, true);

        this.date_time_fields = this.allFields
          .filter((e) => e.inputType === 'DATE' || e.inputType === 'DATE_TIME')
          .map((e) => ({
            id: e._id,
            label: e.label,
            data: e,
          }));
      },

      toBeEstablishedRelationships() {
        this.otherAddActions = this.mapOtherAddEntities(this.localTrigger?.then, []);
      },

      mapOtherAddEntities(form = [], otherAddActions) {
        const otherAdd = [];

        const processItem = (item) => {
          if (item.type === 'ACTION' && item.action === 'other/ADD') {
            otherAdd.push(item.keyIndex);
          } else if (item.type === 'CONDITION') {
            if (item.then?.length) {
              this.mapOtherAddEntities(item.then, otherAddActions);
            }
            if (item.else?.length) {
              this.mapOtherAddEntities(item.else, otherAddActions);
            }
          }
        };

        form.forEach(processItem);

        if (otherAdd.length > 1) {
          otherAddActions.push(otherAdd);
        }

        return otherAddActions;
      },

      handleGlobalActionChange() {
        const firstAction = this.localTrigger?.then?.[0];
        if (!firstAction) return;

        firstAction.action = this.localTrigger.is_global_action
          ? 'file/ADD_FOLDER'
          : 'other/UPDATE';

        if (this.localTrigger.is_global_action) {
          firstAction.type = 'ACTION';
          this.localTrigger.schedule_type = 'Select_manually';
        }
      },

      // Checkbox handlers
      handleCheckMinutesChange(val) {
        this.manual_schedule_settings.minutes = val ? [...this.minutes] : [];
        this.isminutesIndeterminate = false;
      },

      handleCheckedMinuteChange(value) {
        this.updateCheckboxState('minutes', value, this.minutes.length);
      },

      handleCheckHoursChange(val) {
        this.manual_schedule_settings.hours = val ? [...this.hours] : [];
        this.ishoursIndeterminate = false;
      },

      handleCheckedHourChange(value) {
        this.updateCheckboxState('hours', value, this.hours.length);
      },

      handleCheckDaysChange(val) {
        this.manual_schedule_settings.days = val ? [...this.days] : [];
        this.isdaysIndeterminate = false;
      },

      handleCheckedDayChange(value) {
        this.updateCheckboxState('days', value, this.days.length);
      },

      handleCheckWeeksChange(val) {
        this.manual_schedule_settings.days_of_week = val ? [...this.weeks] : [];
        this.isweeksIndeterminate = false;
      },

      handleCheckedWeekChange(value) {
        this.updateCheckboxState('weeks', value, this.weeks.length, 'days_of_week');
      },

      handleCheckMonthsChange(val) {
        this.manual_schedule_settings.months = val ? [...this.months] : [];
        this.ismonthsIndeterminate = false;
      },

      handleCheckedMonthChange(value) {
        this.updateCheckboxState('months', value, this.months.length);
      },

      handleCheckYearsChange(val) {
        this.manual_schedule_settings.years = val ? [...this.years] : [];
        this.isyearsIndeterminate = false;
      },

      handleCheckedYearChange(value) {
        this.updateCheckboxState('years', value, this.years.length);
      },

      updateCheckboxState(type, value, totalLength) {
        const checkedCount = value.length;
        this.manual_schedule_settings[`checkAll${type}`] = checkedCount === totalLength;
        this[`is${type}Indeterminate`] = checkedCount > 0 && checkedCount < totalLength;
      },

      setExecutiontime(value) {
        this.recurrence_settings.execution_time = value === 'hours' ? '00:05' : '05:00';
      },

      getTimeZone() {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        this.manual_schedule_settings.time_zone =
          timezone === 'Asia/Calcutta' ? 'Asia/Kolkata' : timezone;
        return timezone;
      },

      resetManualSchedule() {
        this.manual_schedule_settings = {
          minutes: [0],
          hours: [0],
          days: [1],
          months: [...monthsArray],
          days_of_week: [...weeksArray],
          years: [2024, 2025],
          checkAllminutes: false,
          checkAllhours: false,
          checkAlldays: false,
          checkAllweeks: true,
          checkAllmonths: true,
          checkAllyears: false,
          status: false,
          multiple: false,
          time_zone: this.manual_schedule_settings.time_zone,
        };
      },
    },
    watch: {
      localTrigger: {
        handler() {
          this.toBeEstablishedRelationships();
        },
        deep: true,
        immediate: true,
      },
    },
  };
</script>

<style scoped>
  .trigger-settings {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background: #f8fafc;
    font-family:
      -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  }

  /* Header Styles */
  .settings-header {
    background: white;
    border-bottom: 1px solid #e1e8ed;
    padding: 24px 32px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    max-width: 1200px;
    margin: 0 auto;
  }

  .header-title-section {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex: 1;
  }

  .header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 10px;
    color: white;
    font-size: 20px;
  }

  .header-text {
    flex: 1;
  }

  .header-title {
    margin: 0 0 8px 0;
    font-size: 24px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.2;
  }

  .header-subtitle {
    margin: 0;
    font-size: 14px;
    color: #64748b;
    font-weight: 400;
    line-height: 1.5;
  }

  .header-actions {
    margin-top: 4px;
  }

  .help-btn {
    color: #64748b !important;
    font-size: 14px;
    font-weight: 500;
  }

  .help-btn:hover {
    color: #334155 !important;
    background: #f8fafc !important;
  }

  /* Main Content */
  .settings-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px 32px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
  }

  /* Section Styles */
  .settings-section {
    background: white;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: box-shadow 0.2s ease;
  }

  .settings-section:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px 24px;
    border-bottom: 1px solid #f1f5f9;
    background: #f8fafc;
  }

  .section-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: #64748b;
    border-radius: 6px;
    color: white;
    font-size: 16px;
  }

  .section-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
  }

  .section-content {
    padding: 24px;
  }

  /* Form Styles */
  .form-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .form-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 4px;
  }

  .form-label.required::after {
    content: '*';
    color: #dc2626;
    margin-left: 2px;
  }

  .form-label i {
    color: #64748b;
    font-size: 14px;
  }

  .form-hint {
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }

  .modern-input,
  .modern-select {
    width: 100%;
    border-radius: 6px;
    border: 1px solid #d1d5db;
    transition: all 0.2s ease;
  }

  .modern-input:focus,
  .modern-select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    outline: none;
  }

  /* Trigger Configuration */
  .trigger-configuration {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .trigger-main {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .trigger-advanced {
    border-top: 1px solid #f1f5f9;
    padding-top: 20px;
  }

  /* Advanced Panel */
  .advanced-panel {
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
  }

  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e2e8f0;
    background: white;
    border-radius: 6px 6px 0 0;
  }

  .panel-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }

  .schedule-type-selector {
    display: flex;
    gap: 8px;
  }

  .type-radio {
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: white;
    transition: all 0.2s ease;
  }

  .type-radio:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }

  .type-radio i {
    margin-right: 6px;
    color: #64748b;
  }

  /* Date Field Config */
  .date-field-config {
    padding: 20px;
  }

  .config-section {
    margin-bottom: 24px;
  }

  .config-section:last-child {
    margin-bottom: 0;
  }

  .config-title {
    margin: 0 0 12px 0;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    padding-bottom: 8px;
    border-bottom: 1px solid #f1f5f9;
  }

  .config-content {
    padding-left: 4px;
  }

  /* Execution Rules */
  .execution-rules {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .rule-group {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px;
  }

  .rule-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    font-weight: 600;
    color: #374151;
    font-size: 13px;
  }

  .rule-header i {
    color: #64748b;
  }

  .rule-controls {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .control-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .value-selector {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .option-divider {
    color: #9ca3af;
    font-size: 12px;
    font-weight: 500;
  }

  .field-reference {
    color: #6b7280;
    font-size: 13px;
  }

  .field-reference strong {
    color: #374151;
    font-weight: 600;
  }

  .small-select {
    width: 100px;
  }

  .small-input {
    width: 70px;
  }

  .medium-select {
    width: 120px;
  }

  .medium-input {
    width: 120px;
  }

  .full-width-select {
    width: 100%;
  }

  /* Stopping Condition */
  .stopping-condition {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    padding: 10px 12px;
    background: #f8fafc;
    border-radius: 4px;
    border: 1px solid #e2e8f0;
  }

  .stopping-label {
    font-size: 13px;
    color: #374151;
    font-weight: 500;
  }

  .occurrences-text {
    font-size: 13px;
    color: #6b7280;
  }

  /* Manual Schedule */
  .manual-schedule-config {
    padding: 20px;
  }

  .config-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
  }

  .config-actions {
    display: flex;
    gap: 6px;
  }

  /* Schedule Grid */
  .schedule-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
  }

  .schedule-grid.compact {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    margin-bottom: 0;
  }

  /* Schedule Card */
  .schedule-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    transition: all 0.2s ease;
  }

  .schedule-card:hover {
    border-color: #3b82f6;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
  }

  .schedule-card.compact {
    margin: 0;
  }

  .schedule-card-header {
    padding: 12px 16px;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }

  .schedule-checkbox {
    font-weight: 600;
    color: #374151;
    font-size: 13px;
  }

  .schedule-checkbox i {
    margin-right: 6px;
    color: #64748b;
  }

  .schedule-card-content {
    padding: 12px 16px;
    max-height: 160px;
    overflow-y: auto;
  }

  .checkbox-group {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
    gap: 6px;
  }

  .checkbox-group.compact {
    grid-template-columns: 1fr;
  }

  .uniform-checkbox {
    margin: 0;
    font-size: 12px;
  }

  /* Automation Config */
  .automation-config {
    padding: 20px;
  }

  .config-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 20px;
  }

  .config-item {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .recurrence-controls,
  .time-controls {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .control-prefix {
    font-size: 13px;
    color: #6b7280;
    font-weight: 500;
  }

  /* Advanced Options */
  .advanced-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .option-item {
    padding: 16px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
    transition: all 0.2s ease;
  }

  .option-item:hover {
    border-color: #3b82f6;
    background: white;
  }

  .modern-checkbox {
    width: 100%;
  }

  .checkbox-content {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .checkbox-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: #64748b;
    border-radius: 6px;
    color: white;
    font-size: 16px;
  }

  .checkbox-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
  }

  .checkbox-label {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
  }

  .checkbox-description {
    font-size: 12px;
    color: #6b7280;
    line-height: 1.4;
  }

  /* Footer */
  .settings-footer {
    position: sticky;
    bottom: 0;
    background: white;
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
    z-index: 1000;
  }

  .footer-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 32px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .footer-actions {
    display: flex;
    gap: 10px;
    margin-left: auto;
  }

  .footer-info {
    display: flex;
    align-items: center;
    margin-right: auto;
  }

  .cancel-btn {
    padding: 8px 20px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-weight: 500;
    color: #6b7280;
    font-size: 13px;
    transition: all 0.2s ease;
  }

  .cancel-btn:hover {
    border-color: #9ca3af;
    background: #f9fafb;
    color: #374151;
  }

  .save-btn {
    padding: 8px 20px;
    border-radius: 6px;
    font-weight: 500;
    background: #3b82f6;
    border: none;
    color: white;
    font-size: 13px;
    transition: all 0.2s ease;
  }

  .save-btn:hover {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
  }

  .validation-status {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    padding: 6px 10px;
    border-radius: 4px;
  }

  .validation-status.valid {
    color: #059669;
    background: #f0fdf4;
  }

  .validation-status.warning {
    color: #d97706;
    background: #fffbeb;
  }

  .validation-status.error {
    color: #dc2626;
    background: #fef2f2;
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .settings-content {
      padding: 20px;
    }

    .section-content {
      padding: 20px;
    }

    .form-row {
      grid-template-columns: 1fr;
    }

    .schedule-grid {
      grid-template-columns: 1fr;
    }

    .config-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 768px) {
    .settings-header {
      padding: 20px;
    }

    .header-content {
      flex-direction: column;
      gap: 12px;
    }

    .header-actions {
      align-self: flex-end;
    }

    .settings-content {
      padding: 16px;
      gap: 16px;
    }

    .section-header {
      padding: 16px 20px;
    }

    .section-content {
      padding: 16px;
    }

    .footer-content {
      flex-direction: column;
      gap: 12px;
      align-items: stretch;
    }

    .footer-actions {
      margin-left: 0;
      justify-content: space-between;
    }

    .footer-info {
      margin-right: 0;
      justify-content: center;
    }

    .control-row {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }

    .value-selector {
      width: 100%;
      justify-content: space-between;
    }
  }

  @media (max-width: 480px) {
    .schedule-type-selector {
      flex-direction: column;
    }

    .type-radio {
      width: 100%;
    }

    .footer-actions {
      flex-direction: column;
    }

    .cancel-btn,
    .save-btn {
      width: 100%;
    }
  }

  /* Scrollbar Styling */
  .settings-content::-webkit-scrollbar {
    width: 6px;
  }

  .settings-content::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }

  .settings-content::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  .settings-content::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }

  .schedule-card-content::-webkit-scrollbar {
    width: 4px;
  }

  .schedule-card-content::-webkit-scrollbar-track {
    background: #f8fafc;
  }

  .schedule-card-content::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 2px;
  }
  /* Improved Input Number Styles */
  .date-input-number {
    width: 80px !important;
    min-width: 80px;
  }

  .medium-input-number {
    width: 100px !important;
    min-width: 100px;
  }

  /* Ensure proper spacing in control rows */
  .control-row .el-input-number {
    margin: 0 4px;
  }

  /* Fix for small input containers */
  .value-selector .el-input-number {
    flex-shrink: 0;
  }

  /* Better alignment for number inputs */
  .value-selector {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: nowrap;
  }

  /* Specific fix for stopping condition */
  .stopping-condition .el-input-number {
    width: 70px !important;
    margin: 0 6px;
  }

  /* Recurrence controls spacing */
  .recurrence-controls .el-input-number {
    margin: 0 8px;
  }
</style>
